import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useState } from 'react';
import PageContent from 'components/PageContent';
import Countdown from 'components/CountDown';
import { IFlightTicketProposal } from 'entities/Flights/sdk.ts';
import { useTranslation } from 'react-i18next';
import Modal from 'components/Modal';
import { Field, FieldGroup, FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import TotalSection from 'pages/FlightOffer/TotalSection';
import { FlightOfferViewEnum } from 'pages/FlightOffer/constants.tsx';
import { KeyedMutator } from 'swr';
import { sendInvoiceDetails, useCountries, useInvoiceDetails } from 'api/InvoiceDetails/sdk.ts';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { H2, H4, P } from 'components/ui/typography.tsx';
import { Button } from 'components/ui/button.tsx';
import { MapPinIcon, UserIcon } from '@heroicons/react/20/solid';
import { cn } from 'utils/utils.ts';
import { emailAtom } from 'pages/FlightOffer/EmailAtom';
import { useAtomValue } from 'jotai';
import { t } from 'i18next';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select.tsx';
import { useAuth } from '@/entities/Auth';

interface InvoiceDetailsViewProps {
  flightTicketProposal: IFlightTicketProposal;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const schema = yup.object({
  first_name: yup.string().required(() => t('common.requiredField')),
  last_name: yup.string().required(() => t('common.requiredField')),
  phone: yup.string().required(() => t('common.requiredField')),
  street: yup.string().required(() => t('common.requiredField')),
  house_number: yup.string().required(() => t('common.requiredField')),
  zipcode: yup.string().required(() => t('common.requiredField')),
  city: yup.string().required(() => t('common.requiredField')),
  country_code: yup
    .string()
    .nullable()
    .required(() => t('common.requiredField')),
  email: yup
    .string()
    .email()
    .required(() => t('common.emailIsRequiredLabel')),
});

interface InvoiceDetailsData {
  first_name: string;
  last_name: string;
  phone: string;
  street: string;
  house_number: string;
  zipcode: string;
  city: string;
  country_code: string;
  email: string;
}

export const InvoiceDetailsView = () => {
  const { flightTicketProposal, currency, setCurrency, flightTicketProposalMutate } =
    useOutletContext<InvoiceDetailsViewProps>();
  const { t } = useTranslation();
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const [isInvoiceDataLoading, setIsInvoiceDataLoading] = useState(false);
  const params = useParams<{ flightTicketRequestUUID: string }>();
  const navigate = useNavigate();
  const emailFromAtom = useAtomValue(emailAtom);
  const locationState = useLocation().state ?? {};
  const { userInfo } = useAuth();
  const email = emailFromAtom ?? locationState.email ?? userInfo?.email;
  const { data } = useInvoiceDetails(params.flightTicketRequestUUID!, email!);
  const isOfferExpired = new Date(flightTicketProposal.expiration_datetime) < new Date();
  const { data: countries } = useCountries();
  const {
    formState: { isValid, errors, touchedFields },
    register,
    reset,
    getValues,
    control,
  } = useForm<InvoiceDetailsData>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      street: '',
      house_number: '',
      zipcode: '',
      city: '',
      country_code: '',
      email: '',
    },
  });
  useEffect(() => {
    if (!data || !email) {
      return;
    }

    reset({
      first_name: data.invoice_details.first_name,
      last_name: data.invoice_details.last_name,
      phone: data.invoice_details.phone,
      street: data.invoice_details.street,
      house_number: data.invoice_details.house_number,
      zipcode: data.invoice_details.zipcode,
      city: data.invoice_details.city,
      country_code: data.invoice_details.country_code,
      email,
    });
  }, [data, email, reset]);

  const handleRefreshClick = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCountdownExpire = useCallback(() => {
    setIsExpiredModalOpen(true);
  }, []);

  const handleConfirmAndProceedClick = useCallback(async () => {
    if (isOfferExpired || isInvoiceDataLoading) {
      return;
    }

    try {
      setIsInvoiceDataLoading(true);

      const invoiceDetails = getValues();

      await sendInvoiceDetails(params.flightTicketRequestUUID!, invoiceDetails);

      navigate(`/flights/offer/${params.flightTicketRequestUUID}/payment-methods`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsInvoiceDataLoading(false);
    }
  }, [getValues, isInvoiceDataLoading, isOfferExpired, navigate, params.flightTicketRequestUUID]);

  return (
    <Fragment>
      <PageContent>
        <div className='flex justify-between md:items-center flex-col md:flex-row'>
          <H2>{t('invoiceDetails.invoiceDetails')}</H2>
          <span className='font-[MuseoSansCyrl700] flex md:text-lg md:flex-col max-[300px]:flex-col'>
            <span>{t('flightOffer.offerExpiresIn')}:&nbsp;</span>
            <Countdown
              targetTs={new Date(flightTicketProposal.expiration_datetime).getTime()}
              onExpire={handleCountdownExpire}
            />
          </span>
        </div>
        <div className='w-full bg-lightBeige mt-10 p-10 max-md:p-5 rounded-lg'>
          <FieldGroup>
            <div className='flex flex-row max-md:flex-col justify-between gap-10 w-full min-w-0'>
              <div className='flex flex-col gap-8 w-full min-w-0'>
                <div className='flex flex-row gap-2 items-center'>
                  <UserIcon className='h-6 w-6 text-content-secondary' />
                  <H4 className={cn('font-[MuseoSansCyrl500]')}>{t('invoiceDetails.personalInformation')}</H4>
                </div>
                <Field>
                  <FieldLabel>{t('invoiceDetails.firstName')}</FieldLabel>
                  <Input
                    className={cn(
                      'h-14 border-grey500 w-full min-w-0',
                      touchedFields.first_name && errors.first_name && 'border-red-500'
                    )}
                    {...register('first_name')}
                    type='text'
                    placeholder='e.g. Elias'
                  />
                  {touchedFields.first_name && errors.first_name && (
                    <P className='text-red-500 text-sm mt-1'>{errors.first_name.message}</P>
                  )}
                </Field>
                <Field>
                  <FieldLabel>{t('invoiceDetails.lastName')}</FieldLabel>
                  <Input
                    className={cn(
                      'h-14 border-grey500 w-full min-w-0',
                      touchedFields.last_name && errors.last_name && 'border-red-500'
                    )}
                    {...register('last_name')}
                    type='text'
                    placeholder='e.g. Van der Berg'
                  />
                  {touchedFields.last_name && errors.last_name && (
                    <P className='text-red-500 text-sm mt-1'>{errors.last_name.message}</P>
                  )}
                </Field>
                <Field>
                  <FieldLabel>{t('invoiceDetails.phoneNumber')}</FieldLabel>
                  <Input
                    className={cn(
                      'h-14 border-grey500 w-full min-w-0',
                      touchedFields.phone && errors.phone && 'border-red-500'
                    )}
                    {...register('phone')}
                    type='tel'
                    placeholder='+31 6 1234 5678'
                  />
                  {touchedFields.phone && errors.phone && (
                    <P className='text-red-500 text-sm mt-1'>{errors.phone.message}</P>
                  )}
                </Field>
              </div>
              <div className='flex flex-col gap-8 w-full min-w-0'>
                <div className='flex flex-row gap-2 items-center'>
                  <MapPinIcon className='h-6 w-6 text-content-secondary' />
                  <H4 className={cn('font-[MuseoSansCyrl500]')}>{t('invoiceDetails.billingAddress')}</H4>
                </div>
                <div className='flex flex-row max-sm:flex-col gap-8 w-full min-w-0'>
                  <Field className='w-full min-w-0'>
                    <FieldLabel>{t('invoiceDetails.streetName')}</FieldLabel>
                    <Input
                      className={cn(
                        'h-14 border-grey500 w-full min-w-0',
                        touchedFields.street && errors.street && 'border-red-500'
                      )}
                      {...register('street')}
                      placeholder='Serengeti Way'
                    />
                    {touchedFields.street && errors.street && (
                      <P className='text-red-500 text-sm mt-1'>{errors.street.message}</P>
                    )}
                  </Field>
                  <Field className='w-full sm:max-w-[120px] min-w-0'>
                    <FieldLabel className='text-nowrap'>{t('invoiceDetails.houseNumber')}</FieldLabel>
                    <Input
                      className={cn(
                        'h-14 border-grey500 w-full min-w-0',
                        touchedFields.house_number && errors.house_number && 'border-red-500'
                      )}
                      {...register('house_number')}
                      type='text'
                      placeholder='12-A'
                    />
                    {touchedFields.house_number && errors.house_number && (
                      <P className='text-red-500 text-sm mt-1'>{errors.house_number.message}</P>
                    )}
                  </Field>
                </div>
                <div className='flex flex-row max-sm:flex-col gap-8 w-full min-w-0'>
                  <Field className='w-full sm:max-w-[120px] min-w-0'>
                    <FieldLabel className='text-nowrap'>{t('invoiceDetails.zipCode')}</FieldLabel>
                    <Input
                      className={cn(
                        'h-14 border-grey500 w-full min-w-0',
                        touchedFields.zipcode && errors.zipcode && 'border-red-500'
                      )}
                      {...register('zipcode')}
                      placeholder='1012 AB'
                    />
                    {touchedFields.zipcode && errors.zipcode && (
                      <P className='text-red-500 text-sm mt-1'>{errors.zipcode.message}</P>
                    )}
                  </Field>
                  <Field className='w-full min-w-0'>
                    <FieldLabel>{t('invoiceDetails.city')}</FieldLabel>
                    <Input
                      className={cn(
                        'h-14 border-grey500 w-full min-w-0',
                        touchedFields.city && errors.city && 'border-red-500'
                      )}
                      {...register('city')}
                      type='text'
                      placeholder='Amsterdam'
                    />
                    {touchedFields.city && errors.city && (
                      <P className='text-red-500 text-sm mt-1'>{errors.city.message}</P>
                    )}
                  </Field>
                </div>
                <Field className='w-full min-w-0'>
                  <FieldLabel>{t('invoiceDetails.country')}</FieldLabel>
                  <Controller
                    name='country_code'
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          className={cn(
                            'min-h-14 w-full min-w-0 border border-grey500',
                            touchedFields.country_code &&
                              errors.country_code &&
                              'border-red-500 ring-1 ring-red-500 focus:ring-red-500'
                          )}
                        >
                          <SelectValue placeholder={t('invoiceDetails.selectCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {countries?.map((country) => (
                              <SelectItem key={country.code2} value={country.code2}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {touchedFields.country_code && errors.country_code && (
                    <P className='text-red-500 text-sm mt-1'>{errors.country_code.message}</P>
                  )}
                </Field>
              </div>
            </div>
          </FieldGroup>
        </div>
      </PageContent>
      <TotalSection
        view={FlightOfferViewEnum.INVOICE_DETAILS}
        isConfirmDisabled={!isValid || isInvoiceDataLoading}
        isConfirmLoading={isInvoiceDataLoading}
        flightTicketProposal={flightTicketProposal}
        handleConfirmClick={handleConfirmAndProceedClick}
        currency={currency}
        setCurrency={setCurrency}
        flightTicketProposalMutate={flightTicketProposalMutate}
        backButtonClick={() => navigate(`/flights/offer/${params.flightTicketRequestUUID}/invoice-details/email`)}
      />
      <Modal
        open={isExpiredModalOpen}
        title={t('flightOffer.offerHasExpired')}
        content={
          <div className='w-full'>
            <div className='flex justify-center'>
              <Button onClick={handleRefreshClick}>{t('common.refresh')}</Button>
            </div>
          </div>
        }
      />
    </Fragment>
  );
};
